import gql from 'graphql-tag';
import { ErrorsAttributes } from '../constants';
import { AccountAttributesForRouting } from './accounts';
import { CustomCheckCoreAttributesWithoutSites } from './customChecks';
import { getPolicySetCoreAttributes } from './policySets';
import { ReportTemplateCoreAttributes } from './reports';
import { UserCoreAttributes } from './users';

export const AgencyUserCoreAttributes = `
  ${UserCoreAttributes}
  agencyMembership {
    id
    role
    ssoId
    accountsCount
  }
`;

export const AgencyUserCoreWithAccountsAttributes = `
${AgencyUserCoreAttributes}
memberships(agencyId: $agencyId) {
  id
  role
  issuesFilter
  account {
    id
    name
  }
}
`;

export const AgencyQueryForRouting = gql`
  query AgencyQueryForRouting($agencyId: String!) {
    agency(agencyId: $agencyId) {
      id
      name
      customDomain {
        id
        name
        usesSsoId
        singleSignOnRequired
      }
      accounts {
        id
        name
      }
    }
    accounts {
      nodes {
        ${AccountAttributesForRouting}
      }
    }
    currentUser {
      ${AgencyUserCoreWithAccountsAttributes}
    }
  }
`;

export const AgencyQuery = gql`
  query AgencyQuery($accountId: String!) {
    accountAgency(accountId: $accountId) {
      id
      name
      customDomain {
        id
        name
        usesSsoId
      }
    }
  }
`;

export const AccountsQuery = gql`
  query AgencyAccountsQuery($agencyId: String!) {
    accounts(agencyId: $agencyId) {
      nodes {
        id
        name
        disabled
        membershipsCount
      }
    }
  }
`;

export const AccountQuery = gql`
  query AccountQuery($accountId: String!) {
    account(accountId: $accountId) {
      id
      name
      disabled
      memberships {
        id
        role
        user {
          id
          name
          email
          agencyMembership {
            id
            role
          }
        }
      }
      agencyReportTemplates {
        id
        name
      }
      agencyPolicySets {
        id
        name
      }
    }
  }
`;

export const UpdateAccountMutation = gql`
  mutation UpdateAccount($accountId: String!, $memberships: [MembershipInput!]!, $agencyPolicySetIds: [String!]!, $agencyReportTemplateIds: [String!]!) {
    updateAccount(accountId: $accountId, memberships: $memberships, agencyPolicySetIds: $agencyPolicySetIds, agencyReportTemplateIds: $agencyReportTemplateIds) {
      account {
        id
        name
        disabled
      }
    }
  }
`;

export const UsersQuery = gql`
  query AgencyUsersQuery($agencyId: String!, $page: Int!, $perPage: Int!, $sortBy: UsersSortBy!, $sortOrder: SortOrder!, $search: String) {
    agency(agencyId: $agencyId) {
      id
      users(page: $page, perPage: $perPage, sortBy: $sortBy, sortOrder: $sortOrder, search: $search) {
        currentPage
        perPage
        totalPages
        totalEntries
        nodes {
          id
          name
          email
          isSuperUser
          agencyMembership {
            id
            role
            ssoId
            accountsCount
          }
          membershipsCount(agencyId: $agencyId)
        }
      }
    }
  }
`;

export const UserQuery = gql`
  query UserQuery($agencyId: String!, $userId: String!) {
    user(userId: $userId) {
      ${AgencyUserCoreWithAccountsAttributes}
    }
  }
`;

export const RemoveUsersMutation = gql`
  mutation RemoveUsers($agencyId: String!, $userIds: [String!]!) {
    removeAgencyUsers(agencyId: $agencyId, userIds: $userIds) {
      status
    }
  }
`;

export const AddUserMutation = gql`
  mutation AddAgencyUser($agencyId: String!, $name: String!, $email: String!, $role: Role, $ssoId: String, $accountMemberships: [AccountMembershipInput!]!) {
    addAgencyUser(agencyId: $agencyId, name: $name, email: $email, role: $role, ssoId: $ssoId, accountMemberships: $accountMemberships) {
      user {
        ${AgencyUserCoreWithAccountsAttributes}
        ${ErrorsAttributes}
      }
    }
  }
`;

export const UpdateUserMutation = gql`
  mutation UpdateAgencyUser($agencyId: String!, $userId: String!, $name: String, $email: String, $role: Role, $ssoId: String, $accountMemberships: [AccountMembershipInput!]!) {
    updateAgencyUser(agencyId: $agencyId, userId: $userId, name: $name, email: $email, role: $role, ssoId: $ssoId, accountMemberships: $accountMemberships) {
      user {
        ${AgencyUserCoreWithAccountsAttributes}
        ${ErrorsAttributes}
      }
    }
  }
`;

export const CustomCheckQuery = gql`
  query AgencyCustomCheck($agencyId: String!, $policyId: String!) {
    agency(agencyId: $agencyId) {
      customCheck(id: $policyId) {
        ${CustomCheckCoreAttributesWithoutSites}
      }
    }
  }
`;

export const CreateCustomCheckMutation = gql`
  mutation CreateAgencyCustomCheck($agencyId: String!, $name: String!, $allTrue: Boolean!, $enabled: Boolean!, $notes: String, $category: CustomCheckCategory, $showToContentEditors: Boolean, $rules: [RuleInput!]!) {
    createAgencyCustomCheck(agencyId: $agencyId, name: $name, allTrue: $allTrue, enabled: $enabled, notes: $notes, category: $category, showToContentEditors: $showToContentEditors, rules: $rules) {
      customCheck {
        ${CustomCheckCoreAttributesWithoutSites}
      }
    }
  }
`;

export const PolicySetQuery = gql`
  query PolicySet($agencyId: String!, $policySetId: String!) {
    agency(agencyId: $agencyId) {
      policySet(id: $policySetId) {
        ${getPolicySetCoreAttributes(null)}
        accounts {
          id
          name
        }
      }
    }
  }
`;

export const CreatePolicySetMutation = gql`
  mutation CreateAgencyPolicySet($agencyId: String!, $policySet: PolicySetInput!) {
    createAgencyPolicySet(agencyId: $agencyId, policySet: $policySet) {
      policySet {
        ${getPolicySetCoreAttributes(null)}
      }
    }
  }
`;

export const ReportTemplateQuery = gql`
  query AgencyReportTemplate($agencyId: String!, $reportTemplateId: String!) {
    agency(agencyId: $agencyId) {
      reportTemplate(id: $reportTemplateId) {
        ${ReportTemplateCoreAttributes}
        accounts {
          id
          name
          disabled
        }
      }
    }
  }
`;

// Role hierarchy levels (higher number = higher privilege)
const ROLE_HIERARCHY = {
  'none': 0,
  'viewer': 1,
  'manager': 2,
  'admin': 3
};

export const userRoles = [
  { value: 'none', label: 'Determined at Account Level' },
  { value: 'viewer', label: 'Viewer' },
  { value: 'manager', label: 'Manager' },
  { value: 'admin', label: 'Admin' }
];

export function findRole(value) {
  return userRoles.find(r => r.value === value);
}

function getRoleLevel(role) {
  return ROLE_HIERARCHY[role] || 0;
}

export function getUpgradeableRoles(agencyRole) {
  if (!agencyRole || agencyRole === 'none') {
    return userRoles;
  }

  const agencyRoleLevel = getRoleLevel(agencyRole);
  return userRoles.filter(role => getRoleLevel(role.value) >= agencyRoleLevel);
}

export function upgradeAccountRoleIfNeeded(accountRole, agencyRole) {
  if (!agencyRole || agencyRole === 'none') {
    return accountRole;
  }

  const agencyRoleLevel = getRoleLevel(agencyRole);
  const accountRoleLevel = getRoleLevel(accountRole);

  if (accountRoleLevel < agencyRoleLevel) {
    // Find the first role that matches or exceeds the agency role level
    const upgradedRole = userRoles.find(role => getRoleLevel(role.value) >= agencyRoleLevel);
    return upgradedRole ? upgradedRole.value : accountRole;
  }

  return accountRole;
}
